import { useState, useMemo, useCallback } from 'react';
import { Collaborator, UpdateStatusEntry } from '@/features/Collaborators/Models/collaborator';
import { StatusCard } from '@/components/shared';

export interface CollaboratorStatusCard extends StatusCard {
    field: keyof Omit<UpdateStatusEntry, 'currentStep'>;
}

interface CardFilter {
    field: keyof Omit<UpdateStatusEntry, 'currentStep'>;
    state: 'updated' | 'not_updated';
}

export const useStatusCards = (collaborators: Collaborator[]) => {
    const [cardFilter, setCardFilter] = useState<CardFilter | null>(null);

    // Filtra colaboradores baseado no card selecionado
    const cardFilteredCollaborators = useMemo(() => {
        if (!cardFilter) {
            return collaborators;
        }
        const { field, state } = cardFilter;
        return collaborators.filter(c => {
            const status = c.updatedStatus?.[field]?.status;
            if (state === 'updated') {
                return status === 'UPDATED';
            }
            return status !== 'UPDATED';
        });
    }, [collaborators, cardFilter]);

    // Calcula estatísticas para os cards
    const stats = useMemo(() => {
        const total = collaborators.length;
        if (total === 0) return [];

        const fields: (keyof Omit<UpdateStatusEntry, 'currentStep'>)[] = ['byUser', 'byDHO', 'byDP', 'byTech'];
        const names: { [key in (keyof Omit<UpdateStatusEntry, 'currentStep'>)]: string } = {
            byUser: 'Atualizado por Colaborador',
            byDHO: 'Atualizado pelo DHO',
            byDP: 'Atualizado pelo DP',
            byTech: 'Atualizado pelo Tech'
        };

        const cardStats = fields.map(field => {
            const updated = collaborators.filter(c => c.updatedStatus?.[field]?.status === 'UPDATED').length;
            let filterState: 'updated' | 'not_updated' | 'none' = 'none';
            if (cardFilter?.field === field) {
                filterState = cardFilter.state;
            }
            return {
                name: names[field],
                field,
                stat: `${Math.round((updated / total) * 100)}%`,
                updated,
                total,
                filterState
            } as CollaboratorStatusCard;
        });
        
        console.log('Status cards stats calculated:', { total, cardStats, cardFilter });
        return cardStats;
    }, [collaborators, cardFilter]);

    // Handler para clique nos cards
    const handleCardClick = useCallback((field: keyof Omit<UpdateStatusEntry, 'currentStep'>) => {
        console.log('Status card clicked:', field);
        setCardFilter(prev => {
            const newFilter = prev?.field === field 
                ? (prev.state === 'not_updated' ? { field, state: 'updated' as const } : null)
                : { field, state: 'not_updated' as const };
            console.log('Status card filter changed:', prev, '->', newFilter);
            return newFilter;
        });
    }, []);

    // Reset do filtro de cards
    const resetCardFilter = useCallback(() => {
        setCardFilter(null);
    }, []);

    return {
        cardFilteredCollaborators,
        stats: stats as CollaboratorStatusCard[],
        cardFilter,
        handleCardClick,
        resetCardFilter
    };
}; 